import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  performanceAlarmsQuery,
  performanceQuery,
  type AlarmFlag,
  type DevicePerformanceAlarms,
  type DevicePerformancePage,
  type PerformanceScore,
  type UpgradeAlarm,
} from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Performance And Upgrades - inventoree/devices/others/deviceAlarms.php
 * si alarmsList.php, cu gestul din scriptul inline: radio All/Info/Minor/
 * Major/Critical, primul click pe All la deschidere, button('loading') pe
 * eticheta apasata pana vine lista.
 */

const FLAGS: { value: AlarmFlag; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: '0', label: 'Info' },
  { value: '1', label: 'Minor' },
  { value: '2', label: 'Major' },
  { value: '3', label: 'Critical' },
]

export function DevicePerformance({ id }: ScreenProps) {
  const page = useQuery(performanceQuery(id))
  const [flag, setFlag] = useState<AlarmFlag>('all')
  const alarms = useQuery(performanceAlarmsQuery(id, flag))

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  return (
    <div className="device-performance">
      <Kind page={page.data} />
      <Score score={page.data.score} />
      <Filters
        flag={flag}
        loading={alarms.isFetching ? flag : null}
        onPick={(next) => {
          if (next === flag) void alarms.refetch()
          else setFlag(next)
        }}
      />
      <div className="row">
        <div className="col-md-12 alarmsContainer">
          {alarms.error ? (
            <div className="alert alert-danger" role="alert">
              Something went wrong: {alarms.error.message}
            </div>
          ) : alarms.data ? (
            <AlarmList data={alarms.data} />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function Kind({ page }: { page: DevicePerformancePage }) {
  return (
    <div className="row" style={{ marginBottom: 14 }}>
      <div className="col-md-12">
        <span
          style={{
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: 3,
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.04em',
            color: '#fff',
            background: page.kind.color,
          }}
        >
          {page.kind.label}
        </span>
        {page.kind.note !== '' && (
          <span style={{ marginLeft: 10, fontSize: 12, color: '#6b7480' }}>
            <i className="fas fa-info-circle"></i> {page.kind.note}
          </span>
        )}
      </div>
    </div>
  )
}

function Score({ score }: { score: PerformanceScore | null }) {
  if (!score) return null

  const unknown = score.unknown.map((f) => f.label.toLowerCase()).join(', ')

  return (
    <div className="row" style={{ marginBottom: 18 }}>
      <div className="col-md-12">
        <h4 style={{ margin: '0 0 10px' }}>Configuration score</h4>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: score.color }}>
            {score.score === null ? 'n/a' : score.score}
          </span>
          {score.score !== null && <span style={{ color: '#9aa4b1', fontSize: 13 }}>/ 100</span>}
          <span className={`label ${score.label}`}>{score.band}</span>
        </div>

        {score.costly.length > 0 ? (
          <>
            <p style={{ margin: '0 0 8px', color: '#6b7480', fontSize: 12 }}>
              What holds this machine back, most expensive first. The points are the ones the score is missing because of
              it &mdash; fix the first line and the score climbs by that much.
            </p>
            <ul className="feeds" style={{ marginBottom: 0 }}>
              {score.costly.map((factor) => (
                <li key={factor.key}>
                  <div className="col1">
                    <div className="cont">
                      <div className="cont-col1">
                        <div className="label label-sm" style={{ background: score.color }}>
                          <i className="fas fa-wrench"></i>
                        </div>
                      </div>
                      <div className="cont-col2">
                        <div className="desc">
                          <strong>{factor.label}:</strong> {factor.have} <em>against {factor.want}</em>
                          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: score.color }}>
                            &minus;{factor.cost} points
                          </span>
                          {factor.virtualAge && (
                            <span style={{ marginLeft: 8, fontSize: 11, color: '#9aa4b1' }}>
                              <i className="fas fa-info-circle"></i> virtual BIOS, not the physical host
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        ) : score.score !== null ? (
          <div className="alert alert-success" style={{ marginBottom: 0 }}>
            <strong>
              <i className="far fa-thumbs-up"></i> Nothing to upgrade.
            </strong>{' '}
            Every factor the score looks at is already at target.
          </div>
        ) : null}

        {unknown !== '' && (
          <p style={{ margin: '8px 0 0', color: '#9aa4b1', fontSize: 11 }}>
            <i className="fas fa-question-circle"></i> Not reported by the agent, so left out of the score entirely: {unknown}.
            A score is only as complete as what the machine tells us.
          </p>
        )}
      </div>
    </div>
  )
}

function Filters({
  flag,
  loading,
  onPick,
}: {
  flag: AlarmFlag
  loading: AlarmFlag | null
  onPick: (flag: AlarmFlag) => void
}) {
  return (
    <div className="btn-group select-alarms" data-toggle="buttons">
      {FLAGS.map((item) => {
        const busy = loading === item.value
        return (
          <label
            key={item.value}
            className={`btn btn-theme btn-transparent select-alarm${flag === item.value ? ' active' : ''}${busy ? ' disabled' : ''}`}
            data-loading-text="Loading..."
            onClick={(ev) => {
              ev.preventDefault()
              if (!busy) onPick(item.value)
            }}
          >
            <input type="radio" name="alarmType" autoComplete="off" value={item.value} defaultChecked={item.value === 'all'} />
            {busy ? 'Loading...' : ` ${item.label}`}
          </label>
        )
      })}
    </div>
  )
}

function AlarmList({ data }: { data: DevicePerformanceAlarms }) {
  if (data.alarms.length === 0) {
    return (
      <ul className="feeds">
        <li style={{ background: 'none' }}>
          <div className="alert alert-success">
            <strong>
              <i className="far fa-thumbs-up"></i> Great!
            </strong>{' '}
            No upgrades here.
          </div>
        </li>
      </ul>
    )
  }

  return (
    <ul className="feeds">
      {data.alarms.map((alarm) => (
        <Alarm key={alarm.id} alarm={alarm} />
      ))}
    </ul>
  )
}

function Alarm({ alarm }: { alarm: UpgradeAlarm }) {
  return (
    <li>
      <div className="col1">
        <div className="cont">
          <div className="cont-col1">
            <div className={`label label-sm ${alarm.color}`}>
              <i className="far fa-bell"></i>
            </div>
          </div>
          <div className="cont-col2">
            <div className="desc">
              {alarm.details} <em>{alarm.when}</em>
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: alarm.ageColor }}>
                <i className="far fa-clock"></i> {alarm.age}
              </span>
            </div>
          </div>
        </div>
      </div>
    </li>
  )
}
